import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, Loading, PageHeader, Button, Modal, Badge } from "@/components/common";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState({ modules: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([api.get("/roles"), api.get("/permissions/matrix")]);
      setRoles(r); setMatrix(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (loading) return <Loading />;

  return (
    <>
      <PageHeader title="Peran & Hak Akses" subtitle={`${roles.length} peran`} icon={ShieldCheck}
        actions={<Button onClick={() => setEditing({ permissions: {} })} data-testid="add-role-btn"><Plus size={16} /> Peran Baru</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {roles.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`role-${r.name}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-semibold text-slate-800">{r.label || r.name}</div><div className="text-xs text-slate-400">{r.name}</div></div>
              {r.name !== "OWNER" && <button onClick={() => setEditing(r)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil size={15} /></button>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {r.is_system && <Badge className="bg-slate-100 text-slate-600">Sistem</Badge>}
              <Badge className="bg-primary/10 text-primary">{r.user_count} karyawan</Badge>
              <Badge className="bg-slate-100 text-slate-600">{Object.keys(r.permissions || {}).length} modul</Badge>
            </div>
          </Card>
        ))}
      </div>
      {editing && <RoleModal role={editing} matrix={matrix} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function RoleModal({ role, matrix, onClose, onSaved }) {
  const isNew = !role.id;
  const [name, setName] = useState(role.name || "");
  const [label, setLabel] = useState(role.label || "");
  const [perms, setPerms] = useState(role.permissions || {});
  const [busy, setBusy] = useState(false);

  const toggle = (mod, act) => {
    setPerms((p) => {
      const cur = new Set(p[mod] || []);
      cur.has(act) ? cur.delete(act) : cur.add(act);
      return { ...p, [mod]: [...cur] };
    });
  };
  const has = (mod, act) => (perms[mod] || []).includes(act);

  const save = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(perms).filter(([, a]) => a.length));
      if (isNew) await api.post("/roles", { name, label, permissions: clean });
      else await api.patch(`/roles/${role.id}`, { label, permissions: clean });
      toast.success("Peran disimpan"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Peran Baru" : `Edit ${role.label || role.name}`} maxWidth="max-w-3xl">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Nama (kode)</span><input value={name} onChange={(e) => setName(e.target.value)} disabled={!isNew} className="w-full h-10 px-3 rounded-xl border border-slate-300 disabled:bg-slate-100" data-testid="rf-name" /></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Label</span><input value={label} onChange={(e) => setLabel(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>
        </div>
        <div className="overflow-x-auto border border-slate-100 rounded-xl">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left py-2 px-3 font-medium text-slate-500">Modul</th>
              {matrix.actions.map((a) => <th key={a.key} className="py-2 px-2 font-medium text-slate-500 text-center">{a.label}</th>)}
            </tr></thead>
            <tbody>
              {matrix.modules.map((m) => (
                <tr key={m.key} className="border-b border-slate-50">
                  <td className="py-2 px-3 text-slate-700">{m.label}</td>
                  {matrix.actions.map((a) => (
                    <td key={a.key} className="py-2 px-2 text-center">
                      <input type="checkbox" checked={has(m.key, a.key)} onChange={() => toggle(m.key, a.key)} className="w-4 h-4 accent-emerald-600" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="rf-save">{busy ? "Menyimpan..." : "Simpan Peran"}</Button>
      </div>
    </Modal>
  );
}
